/** Geometry for the hand-rolled chart kinds, kept pure so node-only vitest can pin it. */

/** Whole-number shares of `total` that add up to exactly `total` (largest remainder); a part above zero never rounds to nothing when `keepNonZero`. */
export function largestRemainder(values: number[], total = 100, keepNonZero = false): number[] {
  const sum = values.reduce((s, v) => s + Math.max(0, v), 0)
  if (sum <= 0 || total <= 0) return values.map(() => 0)
  const exact = values.map((v) => (Math.max(0, v) / sum) * total)
  const out = exact.map((x) => Math.floor(x))
  if (keepNonZero) {
    values.forEach((v, i) => {
      if (v > 0 && out[i] === 0) out[i] = 1
    })
  }
  let left = total - out.reduce((s, v) => s + v, 0)
  const order = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i)
  for (let k = 0; left > 0 && order.length > 0; k = (k + 1) % order.length) {
    const i = order[k]!.i
    if (values[i]! > 0) {
      out[i]! += 1
      left -= 1
    }
  }
  while (left < 0) {
    let big = 0
    out.forEach((v, i) => {
      if (v > out[big]!) big = i
    })
    out[big]! -= 1
    left += 1
  }
  return out
}

/** For each of `cells` waffle squares, the index of the part it belongs to, in part order; pass the sum as `cells` for one square per unit. */
export function waffleCells(values: number[], cells = 100): number[] {
  const counts = largestRemainder(values, cells, true)
  return counts.flatMap((n, i) => Array.from({ length: n }, () => i))
}

/** Points of a series across a `width` by `height` box, zero at the bottom edge, first and last points on the side edges. */
export function seriesPoints(values: number[], width: number, height: number): { x: number; y: number }[] {
  const max = Math.max(0, ...values)
  const n = values.length
  return values.map((v, i) => ({
    x: n <= 1 ? width / 2 : (i / (n - 1)) * width,
    y: max <= 0 ? height : height - (Math.max(0, v) / max) * height,
  }))
}

function linePath(points: { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${round(p.x)} ${round(p.y)}`).join(' ')
}

/** The line closed down to the baseline, for the gradient fill under it. */
export function areaPath(points: { x: number; y: number }[], height: number): string {
  if (points.length === 0) return ''
  const first = points[0]!
  const last = points[points.length - 1]!
  return `${linePath(points)} L${round(last.x)} ${round(height)} L${round(first.x)} ${round(height)} Z`
}

function round(n: number): number {
  return Math.round(n * 100) / 100
}

/** Which mark a keyboard key moves the chart's highlight to; `null` clears it. */
export function stepActive(current: number | null, key: string, count: number): number | null {
  if (count <= 0) return null
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return current === null ? 0 : (current + 1) % count
    case 'ArrowLeft':
    case 'ArrowUp':
      return current === null ? count - 1 : (current - 1 + count) % count
    case 'Home':
      return 0
    case 'End':
      return count - 1
    case 'Escape':
      return null
    default:
      return current
  }
}

/** Share of `total` filled, clamped to 0..1; an unknown or zero total is empty, not full. */
export function fraction(value: number, total: number): number {
  if (!(total > 0)) return 0
  return Math.min(1, Math.max(0, value / total))
}

export interface Arc {
  index: number
  start: number
  length: number
  mid: number
}

/** Donut arcs on a 100-unit circumference, a hairline gap between parts; zero parts get no arc. */
export function donutArcs(values: number[], gap = 0.8): Arc[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0)
  if (total <= 0) return []
  const parts = values.filter((v) => v > 0).length
  const g = parts > 1 ? gap : 0
  let at = 0
  const out: Arc[] = []
  values.forEach((v, index) => {
    if (!(v > 0)) return
    const share = (v / total) * 100
    out.push({ index, start: at, length: Math.max(0.3, share - g), mid: at + share / 2 })
    at += share
  })
  return out
}

/** Where a tooltip anchored at `x` percent sits, so it never runs past either edge of its chart. */
export function tipSide(x: number): 'start' | 'center' | 'end' {
  if (x < 22) return 'start'
  if (x > 78) return 'end'
  return 'center'
}

/** A dot plot's axis: a whole-number window a little wider than the data, never below zero, so close values stay apart. */
export function dotDomain(values: number[]): { lo: number; hi: number } {
  if (values.length === 0) return { lo: 0, hi: 1 }
  const min = Math.min(...values)
  const max = Math.max(...values)
  const pad = Math.max(1, (max - min) * 0.25)
  const lo = Math.max(0, Math.floor(min - pad))
  return { lo, hi: Math.max(lo + 1, Math.ceil(max + pad)) }
}

/** Columns for a waffle of `cells` squares: wide rather than tall, ten for a 100-square grid. */
export function waffleColumns(cells: number): number {
  if (cells <= 0) return 1
  if (cells === 100) return 10
  return Math.max(5, Math.min(20, Math.ceil(Math.sqrt(cells * 2.5))))
}

/** The indices of the highest and lowest value; ties go to the earlier one, and a flat or empty series has neither. */
export function peakAndTrough(values: number[]): { peak: number; trough: number } | null {
  if (values.length < 2) return null
  let peak = 0
  let trough = 0
  values.forEach((v, i) => {
    if (v > values[peak]!) peak = i
    if (v < values[trough]!) trough = i
  })
  return values[peak] === values[trough] ? null : { peak, trough }
}

type Pt = { x: number; y: number }

/** Monotone cubic through the points (Fritsch-Carlson, as d3's curveMonotoneX), so the curve never overshoots a value or dips under the baseline. */
export function smoothPath(points: Pt[]): string {
  const n = points.length
  if (n === 0) return ''
  if (n < 3) return linePath(points)
  const dx: number[] = []
  const s: number[] = []
  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1]!.x - points[i]!.x)
    s.push(dx[i]! === 0 ? 0 : (points[i + 1]!.y - points[i]!.y) / dx[i]!)
  }
  const m: number[] = [s[0]!]
  for (let i = 1; i < n - 1; i++) {
    const a = s[i - 1]!
    const b = s[i]!
    m.push(a * b <= 0 ? 0 : (3 * (dx[i - 1]! + dx[i]!)) / ((2 * dx[i]! + dx[i - 1]!) / a + (dx[i]! + 2 * dx[i - 1]!) / b))
  }
  m.push(s[n - 2]!)
  let d = `M${round(points[0]!.x)} ${round(points[0]!.y)}`
  for (let i = 0; i < n - 1; i++) {
    const p = points[i]!
    const q = points[i + 1]!
    const h = dx[i]! / 3
    d += ` C${round(p.x + h)} ${round(p.y + m[i]! * h)} ${round(q.x - h)} ${round(q.y - m[i + 1]! * h)} ${round(q.x)} ${round(q.y)}`
  }
  return d
}

/** The smooth line closed down to the baseline. */
export function smoothAreaPath(points: Pt[], height: number): string {
  if (points.length === 0) return ''
  const first = points[0]!
  const last = points[points.length - 1]!
  return `${smoothPath(points)} L${round(last.x)} ${round(height)} L${round(first.x)} ${round(height)} Z`
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
  index: number
}

function worst(row: number[], side: number): number {
  const sum = row.reduce((a, b) => a + b, 0)
  return Math.max((side * side * Math.max(...row)) / (sum * sum), (sum * sum) / (side * side * Math.min(...row)))
}

/** Squarified treemap (Bruls, Huizing and van Wijk 2000) of `values` in a `width` by `height` box; zero parts get no rect. */
export function squarify(values: number[], width: number, height: number): Rect[] {
  const total = values.reduce((sum, v) => sum + Math.max(0, v), 0)
  if (total <= 0 || width <= 0 || height <= 0) return []
  const scale = (width * height) / total
  const items = values
    .map((v, index) => ({ area: Math.max(0, v) * scale, index }))
    .filter((it) => it.area > 0)
    .sort((a, b) => b.area - a.area || a.index - b.index)
  const out: Rect[] = []
  let x = 0
  let y = 0
  let w = width
  let h = height
  let row: typeof items = []
  const layRow = () => {
    const sum = row.reduce((acc, it) => acc + it.area, 0)
    if (w >= h) {
      const rw = sum / h
      let cy = y
      for (const it of row) {
        const rh = it.area / rw
        out.push({ x, y: cy, w: rw, h: rh, index: it.index })
        cy += rh
      }
      x += rw
      w -= rw
    } else {
      const rh = sum / w
      let cx = x
      for (const it of row) {
        const rw = it.area / rh
        out.push({ x: cx, y, w: rw, h: rh, index: it.index })
        cx += rw
      }
      y += rh
      h -= rh
    }
    row = []
  }
  for (const it of items) {
    const side = Math.min(w, h)
    const areas = row.map((r) => r.area)
    if (row.length === 0 || worst([...areas, it.area], side) <= worst(areas, side)) {
      row.push(it)
    } else {
      layRow()
      row.push(it)
    }
  }
  if (row.length > 0) layRow()
  return out
}

/** Initials for an avatar dot: first letters of the first two words. */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
